import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api-client';
import { API_BASE } from '../api-base';
import { queryKeys } from '../query-client';
import { useMe } from '../me';
import { calendarOwnerKey } from './calendar-storage';
import type { BookingContact, BookingService, BookingSlot } from './booking-card';

async function read<T>(route: 'booking-services' | 'booking-slots' | 'contacts', query: Record<string, string>): Promise<T> {
  // The contacts search goes in a POST body: what the owner typed is a client's name, email or phone, never a logged URL.
  const res =
    route === 'contacts'
      ? await apiFetch(`${API_BASE}/api/calendar/contacts`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(query),
        })
      : await apiFetch(`${API_BASE}/api/calendar/${route}?${new URLSearchParams(query)}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`calendar ${route} ${res.status}`);
  return (await res.json()) as T;
}

/** A site's appointment services, for the card's service step. */
export function useBookingServices(siteId: string | undefined) {
  const ownerKey = calendarOwnerKey(useMe());
  return useQuery({
    queryKey: queryKeys.calendarBookingServices(ownerKey, siteId ?? ''),
    queryFn: async () => (await read<{ services?: BookingService[] }>('booking-services', { siteId: siteId ?? '' })).services ?? [],
    enabled: !!siteId,
  });
}

/** A service's open times with one staff member on one day of the business's clock: the only times Book can book. */
export function useBookingSlots(query: { siteId?: string; serviceId?: string; staffResourceId?: string; day: string; timeZone: string }) {
  const ownerKey = calendarOwnerKey(useMe());
  const { siteId = '', serviceId = '', staffResourceId = '', day, timeZone } = query;
  return useQuery({
    queryKey: queryKeys.calendarBookingSlots(ownerKey, siteId, serviceId, staffResourceId, day, timeZone),
    queryFn: async () =>
      (await read<{ slots?: BookingSlot[] }>('booking-slots', { siteId, serviceId, staffResourceId, day, timeZone })).slots ?? [],
    enabled: !!(siteId && serviceId && staffResourceId && day),
  });
}

/** A site's contacts matching what the owner typed (the newest ones before they type), for the card's client step. */
export function useBookingContacts(siteId: string | undefined, search: string) {
  const ownerKey = calendarOwnerKey(useMe());
  return useQuery({
    queryKey: queryKeys.calendarContacts(ownerKey, siteId ?? '', search),
    queryFn: async () => (await read<{ contacts?: BookingContact[] }>('contacts', { siteId: siteId ?? '', q: search })).contacts ?? [],
    enabled: !!siteId,
    keepPreviousData: true,
  });
}
